"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
} from "@mui/icons-material"
import { useAuth } from "../../contexts/AuthContext"
import webhookService from "../../services/webhook.service"
import { buildSamplePayload } from "../../utils/webhook-samples"
import EmptyState from "../../components/common/EmptyState"
import PageHeader from "../../components/common/PageHeader"

// Predefined webhook events

const WebhookManager = () => {
  const navigate = useNavigate()
  const { user, selectedOrganization: organization } = useAuth()

  // State management
  const [webhooks, setWebhooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [tabValue, setTabValue] = useState(0)
  const [webhookLogs, setWebhookLogs] = useState([])
  const [selectedLog, setSelectedLog] = useState(null)
  const [secretVisibility, setSecretVisibility] = useState({})

  // Dialog states
  const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    events: [],
    isActive: true,
  })

  // Currently selected webhook
  const [currentWebhook, setCurrentWebhook] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [testPayload, setTestPayload] = useState("")
  const [webhookEvents, setWebhookEvents] = useState([])

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  useEffect(() => {
    webhookService.getWebhookEvents().then((res) => setWebhookEvents(res.data.data || []))
  }, [])

  // Load webhooks when organization changes or component mounts
  useEffect(() => {
    fetchWebhooks()
  }, [organization])

  // Add a location change listener to refresh webhooks when returning to this page
  useEffect(() => {
    // This will run when the component mounts
    fetchWebhooks()

    // Listen for navigation events
    const handleLocationChange = () => {
      if (window.location.pathname === "/webhooks") {
        fetchWebhooks()
      }
    }

    // Add event listener
    window.addEventListener("popstate", handleLocationChange)

    // Clean up
    return () => {
      window.removeEventListener("popstate", handleLocationChange)
    }
  }, [])

  // Fetch webhooks from API
  const fetchWebhooks = async () => {
    try {
      setLoading(true)

      // Get the organization ID (either from context or user's default)
      const orgId = organization?._id || user?.organizationId || user?.organization?._id

      if (!orgId) {
        console.error("No organization ID available for fetching webhooks")
        setLoading(false)
        return
      }

      console.log("Fetching webhooks for organization:", orgId)
      const response = await webhookService.getWebhooksByOrganization(orgId)
      console.log("Webhooks response:", response)

      setWebhooks(response.data.data || [])
    } catch (error) {
      console.error("Error fetching webhooks:", error)
      showSnackbar("Failed to fetch webhooks: " + (error.response?.data?.message || error.message), "error")
    } finally {
      setLoading(false)
    }
  }

  // Fetch webhook logs for a specific webhook
  const fetchWebhookLogs = async (webhookId) => {
    try {
      setLoading(true)
      const response = await webhookService.getWebhookDeliveries(webhookId)
      setWebhookLogs(response.data.data || [])
    } catch (error) {
      showSnackbar("Failed to fetch webhook logs", "error")
      console.error("Error fetching webhook logs:", error)
    } finally {
      setLoading(false)
    }
  }

  // Show notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  // Close notification
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  // Handle event selection changes
  const handleEventsChange = (e) => {
    setFormData({ ...formData, events: e.target.value })
  }

  // Handle active toggle
  const handleActiveChange = (event) => {
    setFormData({ ...formData, isActive: event.target.checked })
  }

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      name: "",
      url: "",
      events: [],
      isActive: true,
    })
    setIsEditMode(false)
    setCurrentWebhook(null)
  }

  // Open webhook dialog for creation
  const handleOpenWebhookDialog = () => {
    resetForm()
    setIsWebhookDialogOpen(true)
  }

  // Open webhook dialog for editing
  const handleEditWebhook = (webhook) => {
    setCurrentWebhook(webhook)
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      isActive: webhook.isActive,
    })
    setIsEditMode(true)
    setIsWebhookDialogOpen(true)
  }

  // Close webhook dialog
  const handleCloseWebhookDialog = () => {
    setIsWebhookDialogOpen(false)
    resetForm()
  }

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (webhook) => {
    setCurrentWebhook(webhook)
    setIsDeleteDialogOpen(true)
  }

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false)
    setCurrentWebhook(null)
  }

  // Open log details dialog
  const handleOpenLogDialog = (log) => {
    setSelectedLog(log)
    setIsLogDialogOpen(true)
  }

  // Close log details dialog
  const handleCloseLogDialog = () => {
    setIsLogDialogOpen(false)
    setSelectedLog(null)
  }

  // Open test webhook dialog
  const handleOpenTestDialog = (webhook) => {
    setCurrentWebhook(webhook)

    // events is required, so there's always a first one
    setTestPayload(JSON.stringify(buildSamplePayload(webhook.events[0]), null, 2))
    setIsTestDialogOpen(true)
  }

  // Close test webhook dialog
  const handleCloseTestDialog = () => {
    setIsTestDialogOpen(false)
    setTestPayload("")
  }

  // Submit webhook form (create or update)
  const handleSubmitWebhook = async () => {
    try {
      if (!formData.name || !formData.url || formData.events.length === 0) {
        showSnackbar("Please fill in all required fields and select at least one event", "error")
        return
      }

      // Get the organization ID (either from context or user's default)
      const orgId = organization?._id || user?.organizationId || user?.organization?._id

      if (!orgId) {
        showSnackbar("No organization selected. Please select an organization first.", "error")
        return
      }

      const webhookData = {
        ...formData,
        organizationId: orgId,
      }

      if (isEditMode && currentWebhook) {
        await webhookService.updateWebhook(currentWebhook._id, webhookData)
        showSnackbar("Webhook updated successfully")
      } else {
        await webhookService.createWebhook(webhookData)
        showSnackbar("Webhook created successfully")
      }

      handleCloseWebhookDialog()
      fetchWebhooks()
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to save webhook", "error")
      console.error("Error saving webhook:", error)
    }
  }

  // Delete webhook
  const handleDeleteWebhook = async () => {
    try {
      await webhookService.deleteWebhook(currentWebhook._id)
      showSnackbar("Webhook deleted successfully")
      handleCloseDeleteDialog()
      fetchWebhooks()
    } catch (error) {
      showSnackbar("Failed to delete webhook", "error")
      console.error("Error deleting webhook:", error)
    }
  }

  // Test webhook
  const handleTestWebhook = async () => {
    try {
      if (!currentWebhook) return

      let payload
      try {
        payload = JSON.parse(testPayload)
      } catch (e) {
        showSnackbar("Invalid JSON payload", "error")
        return
      }

      await webhookService.testWebhook(currentWebhook._id, payload)
      showSnackbar("Test webhook sent successfully")
      handleCloseTestDialog()

      // Refresh logs after a short delay to show the new log
      setTimeout(() => {
        if (tabValue === 1) {
          fetchWebhookLogs(currentWebhook._id)
        }
      }, 1000)
    } catch (error) {
      showSnackbar("Failed to test webhook", "error")
      console.error("Error testing webhook:", error)
    }
  }

  // Toggle webhook active state
  const handleToggleActive = async (webhook) => {
    try {
      const updatedWebhook = {
        ...webhook,
        isActive: !webhook.isActive,
      }

      await webhookService.updateWebhook(webhook._id, updatedWebhook)
      showSnackbar(`Webhook ${updatedWebhook.isActive ? "activated" : "deactivated"} successfully`)
      fetchWebhooks()
    } catch (error) {
      showSnackbar("Failed to update webhook status", "error")
      console.error("Error toggling webhook status:", error)
    }
  }

  // Copy webhook secret to clipboard
  const handleCopySecret = (secret) => {
    navigator.clipboard.writeText(secret)
    showSnackbar("Secret copied to clipboard")
  }

  // Toggle secret visibility
  const toggleSecretVisibility = (webhookId) => {
    setSecretVisibility((prev) => ({
      ...prev,
      [webhookId]: !prev[webhookId],
    }))
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)

    // If switching to logs tab and we have a current webhook, fetch its logs
    if (newValue === 1 && currentWebhook) {
      fetchWebhookLogs(currentWebhook._id)
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Never"
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  // Get status color for chips
  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success"
      case "failed":
        return "error"
      default:
        return "default"
    }
  }

  // Loading indicator
  if (loading && webhooks.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Webhook Manager"
        subtitle="Configure and manage webhooks to notify your systems of events in TinyTrack"
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/webhooks/create")}
          >
            Add Webhook
          </Button>
        }
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="webhook tabs">
          <Tab label="Webhooks" />
          <Tab label="Delivery Logs" />
        </Tabs>
      </Paper>

      {/* Webhooks Tab */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button startIcon={<RefreshIcon />} onClick={fetchWebhooks} disabled={loading}>
              Refresh
            </Button>
          </Box>

          {webhooks.length === 0 ? (
            <EmptyState
              title="No webhooks configured"
              description="Add your first webhook to receive event notifications"
              actionText="Add Webhook"
              actionIcon={<AddIcon />}
              onAction={() => navigate("/webhooks/create")}
            />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Secret</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook._id}>
                      <TableCell>{webhook.name}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                        <Tooltip title={webhook.url}>
                          <span>{webhook.url}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 250 }}>
                          {webhook.events.slice(0, 2).map((event) => (
                            <Chip key={event} label={event} size="small" sx={{ fontSize: "0.7rem" }} />
                          ))}
                          {webhook.events.length > 2 && (
                            <Tooltip title={webhook.events.slice(2).join(", ")}>
                              <Chip label={`+${webhook.events.length - 2}`} size="small" sx={{ fontSize: "0.7rem" }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {secretVisibility[webhook._id] ? webhook.secret : "••••••••••••••••"}
                          <IconButton size="small" onClick={() => toggleSecretVisibility(webhook._id)} sx={{ ml: 1 }}>
                            {secretVisibility[webhook._id] ? (
                              <HideIcon fontSize="small" />
                            ) : (
                              <ShowIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton size="small" onClick={() => handleCopySecret(webhook.secret)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={webhook.isActive}
                              onChange={() => handleToggleActive(webhook)}
                            />
                          }
                          label={webhook.isActive ? "Active" : "Inactive"}
                        />
                      </TableCell>
                      <TableCell>{formatDate(webhook.createdAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex" }}>
                          <Tooltip title="Edit webhook">
                            <IconButton size="small" onClick={() => handleEditWebhook(webhook)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test webhook">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenTestDialog(webhook)}
                              disabled={!webhook.isActive}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete webhook">
                            <IconButton size="small" onClick={() => handleOpenDeleteDialog(webhook)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
      )}

      {/* Logs Tab */}
      {tabValue === 1 && (
        <Box>
          {!currentWebhook ? (
            <Card>
              <CardContent>
                <Typography variant="body1" align="center">
                  Select a webhook from the Webhooks tab to view its delivery logs
                </Typography>
              </CardContent>
            </Card>
          ) : webhookLogs.length === 0 ? (
            <EmptyState
              title="No webhook logs"
              description={`No delivery logs found for ${currentWebhook.name}`}
              actionText="Test Webhook"
              actionIcon={<SendIcon />}
              onAction={() => handleOpenTestDialog(currentWebhook)}
              secondaryAction={{
                text: "Refresh",
                icon: <RefreshIcon />,
                onClick: () => fetchWebhookLogs(currentWebhook._id),
              }}
            />
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Logs for: {currentWebhook.name}</Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchWebhookLogs(currentWebhook._id)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Status Code</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Response Time</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhookLogs.map((log) => (
                      <TableRow key={log._id}>
                        <TableCell>{log.event}</TableCell>
                        <TableCell>
                          <Chip label={log.status} color={getStatusColor(log.status)} size="small" />
                        </TableCell>
                        <TableCell>{log.statusCode || "N/A"}</TableCell>
                        <TableCell>{formatDate(log.timestamp)}</TableCell>
                        <TableCell>{log.responseTime ? `${log.responseTime}ms` : "N/A"}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenLogDialog(log)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {loading && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* Create/Edit Webhook Dialog */}
      <Dialog open={isWebhookDialogOpen} onClose={handleCloseWebhookDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? `Edit Webhook: ${currentWebhook?.name}` : "Create New Webhook"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Webhook Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                helperText="A name to help you identify this webhook"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="url"
                label="Endpoint URL"
                value={formData.url}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="https://example.com/webhook"
                helperText="The URL that will receive webhook events"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Events</InputLabel>
                <Select
                  multiple
                  name="events"
                  value={formData.events}
                  onChange={handleEventsChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {webhookEvents.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formData.isActive} onChange={handleActiveChange} color="primary" />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWebhookDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitWebhook}
            variant="contained"
            disabled={!formData.name || !formData.url || formData.events.length === 0}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the webhook "{currentWebhook?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteWebhook} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Log Dialog */}
      <Dialog open={isLogDialogOpen} onClose={handleCloseLogDialog} maxWidth="md" fullWidth>
        <DialogTitle>Webhook Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Event</Typography>
                <Typography variant="body1">{selectedLog.event}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Status</Typography>
                <Chip label={selectedLog.status} color={getStatusColor(selectedLog.status)} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Status Code</Typography>
                <Typography variant="body1">{selectedLog.statusCode || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Time</Typography>
                <Typography variant="body1">{formatDate(selectedLog.timestamp)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Response Time</Typography>
                <Typography variant="body1">
                  {selectedLog.responseTime ? `${selectedLog.responseTime}ms` : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Request Payload</Typography>
                <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mt: 1 }}>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap", overflowX: "auto" }}>
                    {JSON.stringify(selectedLog.payload, null, 2) || "No payload data"}
                  </pre>
                </Paper>
              </Grid>
              {selectedLog.response && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Response</Typography>
                  <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mt: 1 }}>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", overflowX: "auto" }}>
                      {JSON.stringify(selectedLog.response, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              {selectedLog.error && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Error</Typography>
                  <Paper sx={{ p: 2, bgcolor: "#fff8f8", mt: 1, border: "1px solid #ffcccc" }}>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", overflowX: "auto", color: "#d32f2f" }}>
                      {selectedLog.error}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Webhook Dialog */}
      <Dialog open={isTestDialogOpen} onClose={handleCloseTestDialog} maxWidth="md" fullWidth>
        <DialogTitle>Test Webhook: {currentWebhook?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Edit the payload below and click "Send" to test the webhook. This will send a POST request to your endpoint.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={testPayload}
            onChange={(e) => setTestPayload(e.target.value)}
            sx={{ fontFamily: "monospace" }}
            InputProps={{
              sx: { fontFamily: "monospace" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog}>Cancel</Button>
          <Button
            onClick={handleTestWebhook}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!testPayload.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default WebhookManager

